import type { BaumDaten } from "./baum-layout";

/**
 * Wer um eine Person herum steht - aus den Daten, die der Baum ohnehin
 * mitbringt.
 *
 * Die Baumantwort liefert `kanten` (wer in welcher Familie welche Rolle
 * hat) fuer alle geladenen Personen. Daraus laesst sich im Browser
 * ausrechnen, was sonst eine eigene Abfrage je Person braeuchte: Eltern,
 * Partner, Anzahl der Kinder. Das ist genau die Zeile, die in einer
 * Baumdarstellung fehlt, weil dort immer nur ein Ausschnitt gezeichnet
 * ist.
 *
 * WICHTIG: `kanten` enthaelt nur Zeilen der GELADENEN Personen. Das sind
 * zwei Beschneidungen auf einmal.
 *
 * Die eine ist die Sichtbarkeit, und die ist richtig so: wen der Benutzer
 * nicht sehen darf, den liefert der Server gar nicht erst, und er wird
 * hier auch nicht mitgezaehlt.
 *
 * Die andere ist der Radius, und die macht Angaben unehrlich. Wer selbst
 * am Rand des geladenen Umkreises steht, dessen Nachbarn liegen einen
 * Schritt weiter - also ausserhalb. Bei einem Elternpaar stuende dann
 * „Eltern: Maria", bei vier Kindern gar nichts. Beides liest sich als
 * Aussage ueber die Familie, ist aber bloss der Rand der Abfrage. Deshalb
 * `vollstaendig`: nur wer mindestens einen Schritt INNERHALB des Randes
 * liegt, bekommt die Zeile ueberhaupt.
 */
export interface Umfeld {
  elternIds: number[];
  partnerIds: number[];
  /** Nur die geladenen und sichtbaren Kinder. */
  kinder: number;
  /**
   * Koennen die Kanten dieser Person vollstaendig geladen sein? Nur dann
   * darf die Karte Eltern, Partner und Kinderzahl behaupten.
   */
  vollstaendig: boolean;
}

export function umfeldKarte(
  daten: {
    personen: { id: number; entfernung?: { blut: number; schwieger: number } }[];
    kanten: BaumDaten["kanten"];
  },
  /**
   * Der Umkreis, mit dem geladen wurde. Ohne ihn gilt niemand als
   * vollstaendig - lieber keine Zeile als eine halbe.
   */
  umkreis?: number,
): Map<number, Umfeld> {
  // Mitglieder je Familie, einmal aufgebaut statt je Person gesucht.
  const familie = new Map<number, { elternteil: number[]; kind: number[] }>();
  for (const k of daten.kanten) {
    let f = familie.get(k.familieId);
    if (!f) {
      f = { elternteil: [], kind: [] };
      familie.set(k.familieId, f);
    }
    f[k.rolle].push(k.personId);
  }

  const karte = new Map<number, Umfeld>();
  for (const p of daten.personen) {
    /*
     * Eine Person auf Blutsabstand d hat ihre Verwandten auf d+1; die
     * sind nur geladen, wenn d+1 noch in den Umkreis passt. Bei
     * Angeheirateten hoert die Abfrage ohnehin nach einer Partnerkante
     * auf - deren eigene Familie fehlt also immer.
     */
    const e = p.entfernung;
    const vollstaendig =
      umkreis !== undefined && e !== undefined && e.schwieger === 0 && e.blut + 1 <= umkreis;
    karte.set(p.id, { elternIds: [], partnerIds: [], kinder: 0, vollstaendig });
  }

  // Mengen statt Listen: wer in zwei Familien Kind ist (leiblich und
  // adoptiert), haette sonst denselben Elternteil doppelt - und ein Kind,
  // das in zwei Familien derselben Person vorkommt, wuerde zweimal
  // gezaehlt.
  const eltern = new Map<number, Set<number>>();
  const partner = new Map<number, Set<number>>();
  const kinder = new Map<number, Set<number>>();
  const merken = (m: Map<number, Set<number>>, wer: number, wen: number) => {
    let s = m.get(wer);
    if (!s) m.set(wer, (s = new Set()));
    s.add(wen);
  };

  for (const f of familie.values()) {
    for (const kind of f.kind) {
      for (const e of f.elternteil) merken(eltern, kind, e);
    }
    for (const e of f.elternteil) {
      for (const anderer of f.elternteil) if (anderer !== e) merken(partner, e, anderer);
      for (const kind of f.kind) merken(kinder, e, kind);
    }
  }

  for (const [id, u] of karte) {
    u.elternIds = [...(eltern.get(id) ?? [])];
    u.partnerIds = [...(partner.get(id) ?? [])];
    u.kinder = kinder.get(id)?.size ?? 0;
  }
  return karte;
}
